'use client';

import { create } from 'zustand';
import type {
  StaffCallState,
  IncomingCallData,
  ActiveCallData,
  CallStatePayload,
} from '../types/calls.types';
import {
  initMediaDevice,
  createSendTransport,
  createRecvTransport,
  produceMicAudio,
  consumeRemoteAudio,
  toggleMute as msToggleMute,
  closeMediaSession,
} from '@/core/lib/mediasoup-device';

type SocketEmit = (
  event: string,
  data: unknown,
  cb: (res: Record<string, unknown>) => void
) => void;

interface StaffCallStore {
  state: StaffCallState;
  incomingCall: IncomingCallData | null;
  activeCall: ActiveCallData | null;
  isMuted: boolean;
  /**
   * True once the gateway confirms a recorder is running on this call.
   *
   * Set from `call:recording-started`, never assumed from policy: the policy
   * says recording is ALLOWED, the event says it is HAPPENING. Showing a badge
   * for an intent that failed would be its own kind of dishonesty.
   */
  isRecording: boolean;
  audioLevel: number;
  activeElsewhere: boolean;
  socketEmit: SocketEmit | null;

  /**
   * Property-wide ringing/queue snapshot from the gateway.
   *
   * Drives the front-desk call bar: who is ringing, and how many callers are
   * waiting. Null until the first `call:state` arrives.
   */
  callState: CallStatePayload | null;

  /** Set when a transfer is refused, so the active-call screen can say why. */
  transferError: string | null;

  setIncoming: (data: IncomingCallData) => void;
  /**
   * Dismiss a ringing call this staff member is no longer expected to answer
   * — someone else picked it up, or the gateway reported it already taken.
   *
   * Distinct from `declineCall`: that tells the gateway we refused, which
   * counts toward the decline limit and re-routes. This is local cleanup for
   * a call that is no longer ours to answer.
   */
  clearIncoming: (callId?: string) => void;
  acceptCall: (staffId: string) => void;
  declineCall: () => void;
  /**
   * Ring a guest from the hotel — the outbound direction.
   *
   * `guestUserId` is the marketplace identity (`guest_users.id`), not the
   * per-hotel `guests.id`: the gateway authorises it against `guest_hotels`,
   * which keys on the former. Passing the wrong one is refused as
   * "no booking with this hotel", which reads like a data problem rather than
   * a wiring one — hence this note.
   *
   * The property is NOT a parameter. The gateway takes it from the staff
   * member's authenticated socket, exactly as it does for an inbound call, and
   * accepting one here would invite the caller to supply it.
   */
  startOutboundCall: (
    staffId: string,
    guest: { guestUserId: string; name: string; bookingId?: string },
  ) => void;
  /** Set when an outbound call is refused, so the guest list can say why. */
  outboundError: string | null;
  clearOutboundError: () => void;
  /**
   * The gateway refused an outbound call, or it failed to connect.
   *
   * Clears the in-flight call as well as recording the reason: leaving
   * `activeCall` set would hold this staff member's line against a call that
   * does not exist, so they could not place another.
   */
  setOutboundFailed: (reason: string) => void;
  /**
   * Claim a call currently ringing another staff member.
   *
   * `meta` carries the caller details the registry already knows, so the
   * active-call screen can render immediately instead of showing placeholders
   * until the gateway responds.
   */
  pickupCall: (
    callId: string,
    staffId: string,
    meta?: { callerName?: string; hotelName?: string; bookingId?: string },
  ) => void;
  setConnected: () => void;
  endCall: () => void;
  toggleMute: () => void;
  setAudioLevel: (level: number) => void;
  setActiveElsewhere: (v: boolean) => void;
  setCallState: (payload: CallStatePayload) => void;
  /**
   * Replace the local callId with the one the SERVER assigned.
   *
   * An outbound callId is generated client-side so the panel can appear
   * immediately, but the gateway replaces it when it collides with a live call
   * and reports the real one back on `call:state`. Without adopting it, every
   * later action (hang up, mute, transfer) names a call the server does not
   * have.
   */
  adoptCallId: (callId: string) => void;
  /**
   * Rebuild the media session for a call that is still live after the socket
   * dropped and came back.
   *
   * A reconnect restores SIGNALLING only. The mediasoup transports were bound
   * to the old socket and are gone, so without this the staff member is
   * reconnected, the panel says "connected", and neither party can hear
   * anything — the worst shape a call failure can take, because nothing on
   * screen is wrong.
   */
  restoreMediaAfterReconnect: () => void;
  /** The gateway confirmed a recorder is running on this call. */
  markRecording: (callId?: string) => void;
  setTransferFailed: (reason: string) => void;
  clearTransferError: () => void;
  setSocketEmit: (emit: SocketEmit) => void;
  reset: () => void;
}

/**
 * True while a media rebuild is in flight.
 *
 * Module-scoped rather than store state: this guards a RESOURCE operation, and
 * putting it in the store would re-render every subscriber twice per reconnect
 * for something no component displays.
 *
 * A flapping connection fires `connect` repeatedly, and without this a second
 * rebuild would call `closeMediaSession()` while the first was still awaiting
 * its transports — destroying half-built state and leaving the call with a
 * producer bound to a closed transport. That is silent: the panel says
 * connected and no audio flows.
 */
let mediaRebuildInFlight = false;

export const useStaffCallStore = create<StaffCallStore>((set, get) => ({
  state: 'idle',
  incomingCall: null,
  activeCall: null,
  isMuted: false,
  isRecording: false,
  audioLevel: 0,
  activeElsewhere: false,
  socketEmit: null,
  callState: null,
  transferError: null,
  outboundError: null,

  setIncoming: (data) =>
    set({
      state: 'incoming',
      incomingCall: data,
      // A newly-arriving call has no recorder yet — one starts only after the
      // call is answered. Carrying the previous call's badge in tells the
      // staff member the ringing call is already being recorded.
      isRecording: false,
    }),

  clearIncoming: (callId) =>
    set((s) => {
      // A call we already pressed Accept on.
      //
      // `acceptCall` moves to 'connecting' and nulls `incomingCall`, so the
      // guard below would no-op and the screen would sit on "Connecting…"
      // forever — which is exactly the state a LOSER of an accept race is in
      // when the gateway replies `call:already-taken`. Two staff answering at
      // once is routine (it is what the pickup feature produces), so this is
      // the common path, not an edge case.
      if (s.activeCall && (!callId || s.activeCall.callId === callId)) {
        if (s.state === 'connecting') {
          closeMediaSession();
          return {
            ...s,
            state: 'idle',
            activeCall: null,
            incomingCall: null,
            isRecording: false,
          };
        }
        // Already 'connected' — we won and are on the call. A late
        // `call:picked-up` for it must not tear down live media.
        return s;
      }

      // Ignore a stale dismissal for a call we are no longer showing —
      // otherwise a late `call:picked-up` for an earlier call would wipe the
      // panel for a NEW call that has just started ringing.
      if (!s.incomingCall) return s;
      if (callId && s.incomingCall.callId !== callId) return s;
      return { ...s, state: 'idle', incomingCall: null };
    }),

  setCallState: (payload) => set({ callState: payload }),

  adoptCallId: (callId) => {
    const s = get();
    // Only while a call of ours is in flight, and only when it actually
    // differs — this event also fires on the normal (non-colliding) path.
    if (!s.activeCall || s.activeCall.callId === callId) return;
    set({ activeCall: { ...s.activeCall, callId } });
  },

  restoreMediaAfterReconnect: () => {
    const s = get();
    // Only a call that is actually up. A ringing call has no room yet, and an
    // ended one must not resurrect its media.
    if (!s.activeCall || s.state !== 'connected') return;
    if (!s.socketEmit) return;
    // Refuse a second rebuild while one is still building.
    if (mediaRebuildInFlight) return;

    const emit = s.socketEmit;
    const callId = s.activeCall.callId;
    mediaRebuildInFlight = true;

    // Drop whatever is left of the dead session first — the old transports
    // cannot be reused and holding them leaks their ports.
    closeMediaSession();

    initMediaDevice(callId, emit)
      .then(() => createSendTransport(callId, emit))
      .then(() => createRecvTransport(callId, emit))
      .then(() => produceMicAudio())
      .then(() => {
        // RE-CHECK: the call may have ENDED while we were rebuilding.
        //
        // The four awaits above are round trips to the gateway, and End Call is
        // on screen throughout — the staff member can hang up, the guest can
        // hang up, or the gateway can end it. Without this the transports we
        // just built are handed to a call that no longer exists: a live
        // microphone producer at the front desk, with no panel and no way to
        // stop it, for a conversation everyone believes is over.
        const now = get();
        if (now.state !== 'connected' || now.activeCall?.callId !== callId) {
          closeMediaSession();
          return;
        }

        // RESTORE MUTE.
        //
        // `produceMicAudio` creates a fresh, UNMUTED track, so a staff member
        // who muted before switching tabs came back BROADCASTING — with the
        // button still showing muted, so they had no way to know. At a front
        // desk that means the guest hears whatever was said in the room while
        // the line was believed muted.
        if (now.isMuted) msToggleMute(true);
      })
      .catch(() => {
        // The room is gone (the gateway released it while we were away) or
        // the transports cannot be rebuilt. Ending is honest: a call that
        // cannot carry audio is over, and saying so lets the staff member
        // call back rather than talk into silence.
        set({ state: 'ended', activeCall: null, isRecording: false });
        closeMediaSession();
      })
      // Released on BOTH paths, or one failed rebuild latches the call out of
      // ever being repaired again.
      .finally(() => {
        mediaRebuildInFlight = false;
      });
  },

  markRecording: (callId) => {
    const s = get();
    // Only while a call is up.
    if (!s.activeCall) return;

    // And only for THIS call. The gateway sends the callId and it was being
    // discarded — so a `call:recording-started` that arrives late (the recorder
    // takes a moment to come up, and the previous call may have ended in that
    // window) would badge whatever call happens to be on screen NOW. That
    // states a recording is running on a call where none may be, which is the
    // same false assurance the badge exists to prevent.
    if (callId && callId !== s.activeCall.callId) return;

    set({ isRecording: true });
  },

  setTransferFailed: (reason) => set({ transferError: reason }),

  /**
   * Clear a previously-reported transfer failure.
   *
   * Called when a new transfer is attempted: without it a retry shows the
   * PREVIOUS reason until the gateway answers, so a staff member retrying
   * after the target came online still reads "Target staff not available".
   */
  clearTransferError: () => set({ transferError: null }),

  acceptCall: (staffId) => {
    const s = get();
    if (!s.incomingCall) return;

    const callId = s.incomingCall.callId;

    set({
      state: 'connecting',
      activeCall: {
        callId,
        direction: 'inbound',
        guestName: s.incomingCall.guestName,
        hotelName: s.incomingCall.hotelName,
        bookingId: s.incomingCall.bookingId,
        staffId,
        state: 'connecting',
        startedAt: Date.now(),
        isMuted: false,
        audioLevel: 0,
      },
      incomingCall: null,
    });

    if (s.socketEmit) {
      const emit = s.socketEmit;

      // Tell the gateway FIRST.
      //
      // Without this the server never learns the call was answered: it builds
      // no mediasoup room, sends the guest no `call:answer`, leaves the
      // `call_events` row at RINGING, and its ring deadline later marks the
      // call abandoned — while this screen sits showing "connected". The
      // media setup below consumes the room this emit creates, so ordering
      // matters.
      emit('call:accept', { callId, consent: true }, () => {});

      initMediaDevice(callId, emit)
        .then(() => createSendTransport(callId, emit))
        .then(() => createRecvTransport(callId, emit))
        .then(() => produceMicAudio())
        .then(() => {
          set((prev) => ({
            state: 'connected',
            activeCall: prev.activeCall
              ? { ...prev.activeCall, state: 'connected', connectedAt: Date.now() }
              : null,
          }));
        })
        .catch(() => {
          set({ state: 'ended', activeCall: null, isRecording: false });
          closeMediaSession();
        });
    }
  },

  startOutboundCall: (staffId, guest) => {
    const s = get();

    // One line per staff member. Without this a second click while the first
    // call is still ringing would replace `activeCall`, orphaning the first
    // call on the server — it keeps ringing the guest and holds a capacity
    // slot that this client no longer has any way to release.
    if (s.state !== 'idle' && s.state !== 'ended') return;
    if (!s.socketEmit) return;

    const emit = s.socketEmit;
    // Client-generated so the UI can show the call immediately. The gateway
    // replaces it if it collides with a live call, and every event it sends
    // back carries the id it actually used.
    const callId = `out-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    set({
      state: 'connecting',
      outboundError: null,
      // A new call starts with no recording. Left unset, a staff member who
      // had just finished a RECORDED call saw "Recording" on the next one they
      // dialled — before any recorder could possibly have started, and
      // regardless of whether one ever would.
      isRecording: false,
      activeCall: {
        callId,
        direction: 'outbound',
        // The person on the other end, which on an outbound call is the guest.
        guestName: guest.name,
        hotelName: '',
        bookingId: guest.bookingId ?? '',
        staffId,
        state: 'connecting',
        startedAt: Date.now(),
        isMuted: false,
        audioLevel: 0,
      },
      incomingCall: null,
    });

    emit(
      'call:request-outbound',
      { callId, guestUserId: guest.guestUserId, bookingId: guest.bookingId },
      () => {},
    );

    // Media is NOT set up here.
    //
    // The guest has not answered yet, and the mediasoup room does not exist
    // until they do — `media:get-capabilities` would return null and the
    // transports would fail. `useSignalingConnection` completes the media
    // handshake when `call:answer` arrives, which is the same point the
    // inbound path reaches it from the other side.
  },

  clearOutboundError: () => set({ outboundError: null }),

  setOutboundFailed: (reason) => {
    closeMediaSession();
    set({
      state: 'idle',
      activeCall: null,
      incomingCall: null,
      outboundError: reason,
      // The call is over; the recording badge must not describe the next one.
      isRecording: false,
    });
  },

  declineCall: () => {
    const s = get();
    const callId = s.incomingCall?.callId;

    // Tell the gateway, or the call keeps ringing this staff member until the
    // 60s deadline instead of re-routing to the next available person — and
    // the decline never counts toward the retry limit that eventually queues
    // the caller.
    if (callId && s.socketEmit) {
      s.socketEmit('call:decline', { callId }, () => {});
    }

    set({ state: 'idle', incomingCall: null });
  },

  /**
   * Claim a call that is ringing someone else.
   *
   * Goes through the gateway socket, NOT `POST /hotel/calls/:id/pickup`. That
   * REST route only writes `call_events`: the gateway is never told, so no
   * mediasoup room is created, no audio flows, and its ring deadline later
   * overwrites the row as ABANDONED — all while the screen shows a connected
   * call. The socket path runs the same admission and media setup as a normal
   * answer.
   *
   * The gateway replies `call:already-taken` if someone claimed it first,
   * which clears the optimistic state set here.
   */
  pickupCall: (callId, staffId, meta) => {
    const s = get();
    if (!s.socketEmit) return;
    // Already on a call — picking up a second would drop the first.
    if (s.state === 'connected' || s.state === 'connected-muted') return;

    const emit = s.socketEmit;

    set({
      state: 'connecting',
      incomingCall: null,
      activeCall: {
        callId,
        // Pickup only ever claims a call RINGING the property, and only a
        // guest's call rings the property — an outbound call rings one guest.
        direction: 'inbound',
        guestName: meta?.callerName ?? 'Guest',
        hotelName: meta?.hotelName ?? '',
        bookingId: meta?.bookingId ?? '',
        staffId,
        state: 'connecting',
        startedAt: Date.now(),
        isMuted: false,
        audioLevel: 0,
      },
    });

    emit('call:pickup', { callId }, () => {});

    initMediaDevice(callId, emit)
      .then(() => createSendTransport(callId, emit))
      .then(() => createRecvTransport(callId, emit))
      .then(() => produceMicAudio())
      .then(() => {
        set((prev) => ({
          state: 'connected',
          activeCall: prev.activeCall
            ? { ...prev.activeCall, state: 'connected', connectedAt: Date.now() }
            : null,
        }));
      })
      .catch(() => {
        set({ state: 'ended', activeCall: null, isRecording: false });
        closeMediaSession();
      });
  },

  setConnected: () => {
    const s = get();
    if (!s.activeCall) return;
    set({
      state: 'connected',
      activeCall: {
        ...s.activeCall,
        state: 'connected',
        connectedAt: Date.now(),
      },
    });
  },

  endCall: () => {
    closeMediaSession();
    // `transferError` cleared with the call. Left set, the next call opens its
    // panel already showing a failure from a previous conversation.
    //
    // `outboundError` for the same reason, and it is the newer hazard: it is
    // rendered as a toast by `IncomingCallHost`, which clears it on read — but
    // only if that effect runs. A call ended before the toast fired would
    // leave the message queued to appear over the NEXT call, attributing an
    // old refusal to a new guest.
    set({
      state: 'ended',
      activeCall: null,
      transferError: null,
      outboundError: null,
      // Clear the badge with the call, or the NEXT call shows a recording
      // indicator before its recorder has confirmed — or when none starts.
      isRecording: false,
    });
  },

  toggleMute: () => {
    const s = get();
    const newMuted = !s.isMuted;
    msToggleMute(newMuted);
    set({
      isMuted: newMuted,
      state: newMuted ? 'connected-muted' : 'connected',
    });
  },

  setAudioLevel: (level) => set({ audioLevel: level }),

  setActiveElsewhere: (v) => set({ activeElsewhere: v }),

  setSocketEmit: (emit) => set({ socketEmit: emit }),

  reset: () => {
    closeMediaSession();
    set({
      state: 'idle',
      incomingCall: null,
      activeCall: null,
      isMuted: false,
      isRecording: false,
      audioLevel: 0,
      activeElsewhere: false,
      socketEmit: null,
    });
  },
}));

export function useStaffCallClient() {
  return useStaffCallStore();
}

export function handleNewProducer(
  callId: string,
  producerUserId: string,
  emit: SocketEmit
): void {
  consumeRemoteAudio(callId, producerUserId, emit).catch(() => {});
}

/**
 * Negotiate media for an OUTBOUND call, once the guest has answered.
 *
 * The inbound path does this inside `acceptCall`, because that is the moment
 * the mediasoup room exists — the staff member pressing Accept is what creates
 * it. Outbound inverts that: the room is created when the GUEST accepts, so
 * this side has nothing to negotiate against until `call:answer` arrives.
 *
 * Doing it any earlier fails in a way that is easy to misread:
 * `media:get-capabilities` answers `{ rtpCapabilities: null }` for a call the
 * caller is not yet a participant of (a deliberate anti-oracle), so the
 * transports never build and the call connects to silence with nothing logged.
 */
export function setupOutboundMedia(
  callId: string,
  emit: SocketEmit,
  answeredByName?: string,
): void {
  initMediaDevice(callId, emit)
    .then(() => createSendTransport(callId, emit))
    .then(() => createRecvTransport(callId, emit))
    .then(() => produceMicAudio())
    .then(() => {
      useStaffCallStore.setState((prev) => ({
        state: 'connected',
        activeCall: prev.activeCall
          ? {
              ...prev.activeCall,
              state: 'connected',
              connectedAt: Date.now(),
              // The gateway reports who answered; on outbound that is the
              // guest, and it confirms the name the list optimistically showed.
              guestName: answeredByName ?? prev.activeCall.guestName,
            }
          : null,
      }));
    })
    .catch(() => {
      // Almost always a blocked or missing microphone. Naming it saves the
      // staff member from retrying a call that will fail the same way.
      useStaffCallStore
        .getState()
        .setOutboundFailed(
          'Could not access your microphone. Check your browser permissions and try again.',
        );
    });
}
